<script setup lang="ts">

import SearchingAndSuggestionUtil from "@/utils/editView/widgets/SearchingAndSuggestionUtil.ts";
import AttachmentLinkUtil from "@/utils/UIConstants/AttachmentLinkUtil.ts";
import DownloadPurpose from "@/utils/constants/DownloadPurpose.ts";
import ErrorTooltip from "@/components/EditView/ErrorTooltip.vue";
import {WidgetProps} from "@/utils/presenters/interfaces.ts";
import {focusAndSelectInput, NamaNavigateToNextEmit} from "@/utils/common/NamaVueWidget.ts";
import {ref} from "vue";
import {QSelect} from "quasar";
import ExtraCodeFieldWidgetUtil from "@/utils/editView/widgets/ExtraCodeFieldWidgetUtil.ts";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import EntityReferenceData = namaTypes.EntityReferenceData;

const props = defineProps<WidgetProps>();
const fieldId = props.field.id!;
const fieldUtil = new ExtraCodeFieldWidgetUtil(props.presenter, props.fieldMetaData);
const options = ref<EntityReferenceData[]>([]);
const shouldFilter = ref(false);
const loading = ref(false)
const popupVisible = ref(false);
const input = ref<QSelect | null>(null);
const text = fieldUtil.formattedValue();
const emit = defineEmits<NamaNavigateToNextEmit>()

function onSuggest(val, update) {
  update(async () => {
      if (shouldFilter.value) {
        loading.value = true;
        await fieldUtil.suggest(val, options)
        loading.value = false;
      } else {
        options.value = [];
      }
    },
    newRef => {
      if (val !== '' && options.value.length > 0) {
        setTimeout(() => {
          newRef.setOptionIndex(-1)
          newRef.moveOptionSelection(1, true)
        });
      }
    })
}

async function onDownHandler(e) {
  shouldFilter.value = true;
  if (!popupVisible.value) {
    input.value!.updateInputValue(fieldUtil.inputFieldValueRef.value || '', false);
    input.value!.showPopup();
    GUIUtilities.scrollIntoView(input.value?.$el);
  }
}

function onBlur() {
  fieldUtil.onBlur();
  shouldFilter.value = false;
}

function onEnterKeyDown(e: KeyboardEvent) {
  if (popupVisible.value)
    return;
  emit("navigateToNext", {keyboardEvent: e});
}

function focus() {
  focusAndSelectInput(input)
}

function canReceiveFocus() {
  return !fieldUtil.isFieldDisabled(props.forceEnableField);
}

defineExpose({focus, canReceiveFocus})
</script>

<template>
  <q-select
    @keydown.enter="onEnterKeyDown"
    outlined use-input hide-selected fill-input dense hide-bottom-space hide-dropdown-icon options-dense
    :readonly="fieldUtil.isFieldDisabled(forceEnableField)"
    @filter="onSuggest"
    @keydown.down="onDownHandler"
    @dblclick="onDownHandler"
    @popup-hide="popupVisible = false"
    @popup-show="popupVisible = true"
    input-debounce="0"
    :model-value="text"
    :loading="loading"
    :options="options"
    option-label="code"
    ref="input"
    :type="showAsPassword ? 'password' : 'text'"
    @input-value="fieldUtil.onPartialUserInput"
    @update:model-value="fieldUtil.onSelectFromSuggestionList"
    @blur="onBlur" @focus="fieldUtil.onFocus"
    :error="!!props.presenter.fetchFieldErrorMsg(fieldId)"
    style="contain: inline-size"
    class="nama-customize-dense-input tw-w-full"
    :class="{'nama-field-input-disabled':fieldUtil.isFieldDisabled(forceEnableField),'nama-field-input':!fieldUtil.isFieldDisabled(forceEnableField)}"
    input-class="nama-input-class"
    options-selected-class="tw-pl-[6px] tw-pr-[3px] tw-text-base tw-font-normal tw-border-b-black tw-bg-primary-light dark:tw-bg-primary-dark tw-text-white"
    popup-content-class="!tw-max-h-[30vh] tw-font-normal dark:tw-text-white dark:tw-bg-namaBackground-bright-dark">
    <template v-slot:option="scope">
      <q-item v-bind="scope.itemProps">
        <q-item-section v-bind:dir="scope.opt.optionDirection">
          <q-item-label v-text="scope.opt.optionText"/>
          <q-item-label v-if="SearchingAndSuggestionUtil.hasDescriptor(scope.opt)"
                        caption class="dark:tw-text-[rgb(255,255,255,0.5)]" v-text="scope.opt.optionCaption "/>
        </q-item-section>
        <q-item-section avatar v-if="scope.opt.optionHasColorOrImg">
          <q-avatar round class="tw-size-6 compact:tw-size-5 ultra:tw-size-4"
                    :style="scope.opt.colorCode?'backgroundColor:'+scope.opt.colorCode:''">
            <img class="tw-w-full tw-h-full" v-if="SearchingAndSuggestionUtil.shouldShowImgInSuggest(props.presenter.entityType)"
                 :src="AttachmentLinkUtil.downloadURLByRecordId(scope.opt, DownloadPurpose.THUMBNAIL, false)"/>
          </q-avatar>
        </q-item-section>
      </q-item>
    </template>
    <ErrorTooltip v-if="presenter.fetchFieldErrorMsg(fieldId)" :error-msg="presenter.fetchFieldErrorMsg(fieldId)"/>
  </q-select>

</template>

<style scoped>

</style>
